// Fresh rounds: the last day and a half of Hyperliquid, made playable. The newsroom engine finds the
// moves in the most traded markets and the headline that fits each; every move with a good fit,
// whose hour is over, becomes a round on Hyperliquid's own minute candles.
//
// A headline that already says which way the market went ("…sending bitcoin lower") would give the
// round away, so only headlines that do not are used.

import type { WireItem } from "../../shared/newsroom/types.ts";
import { INFO_URL } from "../hl/info";
import type { Bar, Series } from "../news/candles";
import { buildEdition, type Cause, type Story } from "../news/engine";
import { loadUniverse, type Market } from "../news/universe";
import { loadWire } from "../news/wire";
import { shortOf } from "../news/words";
import type { Minute, Place, Round } from "./rounds";
import { DECK_SIZE, REPLAY_MINUTES } from "./rounds";

/** The markets searched, most traded first: what a trader would want to trade on news. */
const SEARCHED = ["BTC", "ETH", "SOL", "HYPE", "SP500", "NDX", "XAU", "XAG", "CL", "BZ", "EUR", "JPY", "NVDA", "TSLA"];
/** How far back: the wire keeps a day and a half of headlines. */
const LOOKBACK_MS = 36 * 3_600_000;
/** Context before the headline, in minutes, fetched in full (the page shows the last hour). */
const BEFORE = 120;

const GIVEAWAY =
  /\b(?:falls?|fell|dips?|dipped|drops?|dropped|slides?|slid|slips?|sinks?|sank|tumbles?|plunges?|plunged|slumps?|sheds?|lower|declines?|retreats?|crash\w*|sell-?off|rout|rises?|rose|jumps?|jumped|surges?|surged|soars?|soared|rall(?:y|ies|ied)|climbs?|gains?|higher|spikes?|spiked|rebounds?|pumps?|dumps?|tanks?)\b/i;

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Hyperliquid's info endpoint, waiting out a rate limit twice before giving up. */
async function info<T>(body: unknown): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    const response = await fetch(INFO_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (response.ok) return (await response.json()) as T;
    if (response.status !== 429 || attempt >= 2) throw new Error(`info ${response.status}`);
    await pause(2_000 * (attempt + 1));
  }
}

type Candle = { t: number; o: string; h: string; l: string; c: string; v: string };

async function fiveMinute(coin: string, now: number): Promise<Series> {
  const raw = await info<Candle[]>({ type: "candleSnapshot", req: { coin, interval: "5m", startTime: now - LOOKBACK_MS, endTime: now } });
  const bars: Bar[] = raw.map((c) => ({ t: c.t, o: Number(c.o), h: Number(c.h), l: Number(c.l), c: Number(c.c), v: Number(c.v) }));
  return { coin, bars, fetchedAt: now };
}

/** The minute candles around a headline, every minute filled (a quiet minute repeats the close). */
async function minutesAround(coin: string, at: number): Promise<{ start: number; bars: Minute[] } | null> {
  const start = Math.floor(at / 60_000) * 60_000 - BEFORE * 60_000;
  const count = BEFORE + REPLAY_MINUTES;
  const raw = await info<Candle[]>({ type: "candleSnapshot", req: { coin, interval: "1m", startTime: start, endTime: start + count * 60_000 - 1 } });
  const byMinute = new Map(raw.map((c) => [c.t, c]));
  const bars: Minute[] = [];
  let last: string | null = null;
  let missing = 0;
  for (let i = 0; i < count; i++) {
    const candle = byMinute.get(start + i * 60_000);
    if (candle) {
      bars.push([candle.o, candle.h, candle.l, candle.c]);
      last = candle.c;
    } else if (last !== null) {
      bars.push([last, last, last, last]);
      missing += 1;
    } else return null;
  }
  return missing > count / 4 ? null : { start, bars };
}

const CATEGORIES: [RegExp, string][] = [
  [/\bCPI\b|inflation|consumer prices?|\bPPI\b|\bPCE\b/i, "Inflation"],
  [/\bFOMC\b|\bFed\b|Federal Reserve|Powell|rate (?:cut|hike|decision)/i, "Fed"],
  [/payrolls?|jobs report|jobless|unemployment/i, "Jobs"],
  [/tariffs?|trade war/i, "Tariffs"],
  [/\b(?:war|missiles?|strikes?|attack\w*|invasion|troops|drone)\b/i, "War"],
  [/earnings|quarterly|guidance|revenue/i, "Earnings"],
  [/\bSEC\b|ETF|regulat/i, "Regulation"],
];

function categoryOf(item: WireItem): string {
  const found = CATEGORIES.find(([pattern]) => pattern.test(item.title));
  if (found) return found[1];
  return item.tags.cryptoWide ? "Crypto" : "News";
}

/** Where a headline turns from the news to what the market did about it. */
const REACTION = /,\s+(?:sending|pushing|lifting|dragging|knocking|boosting|hitting|driving|weighing on|rattling|spooking|sparking)\b|\s+(?:as|after)\s+(?:bitcoin|btc|ether|crypto|stocks|markets?|gold|oil|the dollar|yields|futures|s&p|nasdaq|dow|wall street)\b/i;

/** The news part of a headline: "CPI comes in hot, sending bitcoin lower" → "CPI comes in hot…". */
export function newsPart(title: string): string {
  const cut = title.search(REACTION);
  return cut > 12 ? `${title.slice(0, cut).trim()}…` : title;
}

/** The headline to play: a good fit, not the clock, not a giveaway, not long after the move began. */
function playable(story: Story): Cause | null {
  for (const cause of story.causes) {
    // only a strong fit: a round on a guessed cause is a round about nothing
    if (cause.score < 0.8) break;
    if (cause.item.id.startsWith("cal:")) continue;
    if (GIVEAWAY.test(newsPart(cause.item.title))) continue;
    // a print reported a few minutes after it moved the market is still the news at that minute
    if (cause.lagMin > 15) continue;
    // and the move has to follow within the hour that plays
    if (cause.lagMin < -30) continue;
    return cause;
  }
  return null;
}

/** The least the hour has to swing, from the entry to its furthest point, to be worth playing. */
const SWING: Record<Market["class"], number> = { crypto: 0.008, equities: 0.006, indices: 0.003, commodities: 0.004, fx: 0.0015, rates: 0.002 };

function swingOf(bars: Minute[], from: number): number {
  const entry = Number((bars[from] as Minute)[0]);
  let furthest = 0;
  for (const [, h, l] of bars.slice(from)) furthest = Math.max(furthest, Math.abs(Number(h) / entry - 1), Math.abs(Number(l) / entry - 1));
  return furthest;
}

const HOMES: Record<string, Place> = {
  crypto: { lat: 1.35, lon: 103.82, name: "Singapore" },
  default: { lat: 40.71, lon: -74.01, name: "New York" },
};

export async function freshRounds(now = Date.now()): Promise<Round[]> {
  const [universe, wire] = await Promise.all([loadUniverse(), loadWire()]);
  const markets = SEARCHED.map((underlying) => universe.byUnderlying.get(underlying)?.[0]).filter((market): market is Market => market !== undefined);
  const held: Record<string, Series> = {};
  for (const market of markets) {
    try {
      held[market.coin] = await fiveMinute(market.coin, now);
    } catch {
      // one market fewer
    }
    await pause(150);
  }
  const stories = buildEdition(universe, wire.items, held, now).stories;
  const picked: { story: Story; cause: Cause; market: Market }[] = [];
  for (const story of stories) {
    const market = story.markets[0];
    if (!market || !(market.coin in held)) continue;
    if (story.confidence !== "strong") continue;
    if (now - story.lead.start < (REPLAY_MINUTES + 5) * 60_000) continue;
    const cause = playable(story);
    if (!cause) continue;
    // one round per moment
    if (picked.some((held) => Math.abs(held.story.lead.start - story.lead.start) < 30 * 60_000)) continue;
    picked.push({ story, cause, market });
    if (picked.length >= DECK_SIZE * 2) break;
  }

  const rounds: Round[] = [];
  for (const { story, cause, market } of picked) {
    const at = Math.floor(Math.min(cause.item.ts, story.lead.start) / 60_000) * 60_000;
    if (rounds.length >= DECK_SIZE) break;
    const around = await minutesAround(market.coin, at).catch(() => null);
    if (!around || swingOf(around.bars, BEFORE) < SWING[market.class]) continue;
    const place = cause.item.tags.place;
    rounds.push({
      id: `fresh:${market.coin}:${at}`,
      symbol: shortOf(market.symbol),
      coin: market.coin,
      headline: newsPart(cause.item.title),
      source: cause.item.source,
      at,
      place: place ? { lat: place.lat, lon: place.lon, name: place.name } : ((market.class === "crypto" ? HOMES.crypto : HOMES.default) ?? null),
      category: categoryOf(cause.item),
      reveal: null,
      start: around.start,
      headlineBar: BEFORE,
      bars: around.bars,
      kind: "fresh",
    });
    await pause(150);
  }
  return rounds.sort((a, b) => a.at - b.at);
}
